import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Loader2, Users, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import './Auth.css';

const CATEGORIES = [
  'Fashion', 'Beauty', 'Fitness', 'Travel', 'Food', 'Tech', 
  'Gaming', 'Lifestyle', 'Business', 'Education', 'Music', 'Art'
];

const COUNTRIES = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'India', 'Brazil', 'Mexico', 'Japan', 'Nigeria', 'Ghana', 'Kenya', 'Egypt', 'Morocco', 'South Africa'];

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState('influencer');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone_number: '',
    name: '',
    company_name: '',
    bio: '',
    categories: [],
    country: '',
    state: '',
    city: '',
    social_handles: {
      instagram: '',
      youtube: '',
      tiktok: '',
      x: '',
      facebook: '',
      pinterest: '',
      podcast: ''
    }
  });

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (role === 'influencer' && formData.categories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }

    // Validate at least one social media handle for influencers
    if (role === 'influencer') {
      const hasAtLeastOneSocial = Object.values(formData.social_handles).some(handle => handle && handle.trim() !== '');
      if (!hasAtLeastOneSocial) {
        toast.error('Please provide at least one social media handle');
        return;
      }
    }

    try {
      setLoading(true);
      
      const userData = {
        ...formData,
        role,
        social_handles: role === 'influencer' ? formData.social_handles : undefined,
        bio: role === 'influencer' ? formData.bio : undefined,
        categories: role === 'influencer' ? formData.categories : undefined,
        company_name: role === 'brand' ? formData.company_name : undefined
      };
      
      const user = await register(userData);
      
      toast.success('Registration successful!');
      
      // Redirect based on role
      if (user.role === 'influencer') {
        navigate('/dashboard/influencer');
      } else if (user.role === 'brand') {
        navigate('/dashboard/brand');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content register-content">
        <div className="auth-header">
          <Sparkles className="auth-icon" />
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">Join 1000hints.com today</p>
        </div>

        {/* Role Selection */}
        <div className="role-selection">
          <button
            type="button"
            className={`role-btn ${role === 'influencer' ? 'active' : ''}`}
            onClick={() => setRole('influencer')}
            data-testid="influencer-role-btn"
          >
            <Users size={24} />
            <span>Influencer</span>
          </button>
          <button
            type="button"
            className={`role-btn ${role === 'brand' ? 'active' : ''}`}
            onClick={() => setRole('brand')}
            data-testid="brand-role-btn"
          >
            <Building size={24} />
            <span>Brand</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <Label htmlFor="name">{role === 'brand' ? 'Contact Name' : 'Full Name'}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              placeholder="John Doe"
              data-testid="name-input"
            />
          </div>

          {role === 'brand' && (
            <div className="form-group">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                required
                placeholder="Your Company"
                data-testid="company-input"
              />
            </div>
          )}

          <div className="form-group">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              placeholder="your@email.com"
              data-testid="email-input"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="phone_number">Phone Number *</Label>
            <Input
              id="phone_number"
              type="tel"
              value={formData.phone_number}
              onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
              required
              placeholder="+1 (555) 123-4567"
              data-testid="phone-input"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required
              placeholder="••••••••"
              minLength={6}
              data-testid="password-input"
            />
          </div>

          {role === 'influencer' && (
            <>
              <div className="form-group">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  required
                  placeholder="Tell us about yourself..."
                  rows={3}
                  data-testid="bio-input"
                />
              </div>

              <div className="form-section">
                <Label>Categories *</Label>
                <div className="categories-grid">
                  {CATEGORIES.map((category) => (
                    <label key={category} className="category-item">
                      <Checkbox
                        checked={formData.categories.includes(category)}
                        onCheckedChange={() => handleCategoryToggle(category)}
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <Label>Location *</Label>
                <div className="location-fields">
                  <Select value={formData.country} onValueChange={(value) => setFormData({...formData, country: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="State/Province"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                  />
                  <Input
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-section">
                <Label>Social Media (Optional)</Label>
                <div className="social-inputs">
                  <Input
                    placeholder="Instagram username"
                    value={formData.social_handles.instagram}
                    onChange={(e) => setFormData({...formData, social_handles: {...formData.social_handles, instagram: e.target.value}})}
                  />
                  <Input
                    placeholder="YouTube username"
                    value={formData.social_handles.youtube}
                    onChange={(e) => setFormData({...formData, social_handles: {...formData.social_handles, youtube: e.target.value}})}
                  />
                  <Input
                    placeholder="TikTok username"
                    value={formData.social_handles.tiktok}
                    onChange={(e) => setFormData({...formData, social_handles: {...formData.social_handles, tiktok: e.target.value}})}
                  />
                  <Input
                    placeholder="X (Twitter) username"
                    value={formData.social_handles.x}
                    onChange={(e) => setFormData({...formData, social_handles: {...formData.social_handles, x: e.target.value}})}
                  />
                  <Input
                    placeholder="Facebook username"
                    value={formData.social_handles.facebook}
                    onChange={(e) => setFormData({...formData, social_handles: {...formData.social_handles, facebook: e.target.value}})}
                  />
                  <Input
                    placeholder="Pinterest username"
                    value={formData.social_handles.pinterest}
                    onChange={(e) => setFormData({...formData, social_handles: {...formData.social_handles, pinterest: e.target.value}})}
                  />
                  <Input
                    placeholder="Podcast URL"
                    value={formData.social_handles.podcast}
                    onChange={(e) => setFormData({...formData, social_handles: {...formData.social_handles, podcast: e.target.value}})}
                  />
                </div>
              </div>
            </>
          )}

          <Button 
            type="submit" 
            disabled={loading} 
            className="auth-submit-btn"
            data-testid="register-button"
          >
            {loading ? (
              <>
                <Loader2 className="btn-icon animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        <div className="auth-footer">
          <p>Already have an account? <Link to="/login" className="auth-link">Sign In</Link></p>
          <Link to="/" className="auth-link">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;